import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, EmptyState, Paper, Spinner, Typography } from '@glplus/glpweb-dsl';
import { adminApiClient, type RunnerInsurancePolicy } from '../lib/adminApiClient';
import { InsuranceCard } from '../components/RunnerOnboardingPanels';

/**
 * Insurance cards waiting for review — new applicants and renewals from live
 * Runners alike. A live Runner whose card has expired cannot take new work
 * until one is approved here, so the oldest waits first.
 */
export default function RunnerInsuranceQueue() {
  const [items, setItems] = useState<RunnerInsurancePolicy[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems((await adminApiClient.listRunnerInsurance({ status: 'pending', limit: 50 })).items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the queue');
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (!items && !error) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}><Spinner aria-label="Loading insurance cards" /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Runner insurance</Typography>
      <Typography sx={{ color: '#6b7280', fontSize: '14px', mb: 2 }}>
        Check that the card is readable, the name matches the Runner, and the insurer, policy number and expiry match what
        they typed. A Runner without an approved, current card cannot take new work.
      </Typography>
      {error && <Box sx={{ mb: 2 }}><Alert severity="error">{error}</Alert></Box>}
      {flash && <Box sx={{ mb: 2 }}><Alert severity="success">{flash}</Alert></Box>}
      {items && !items.length ? (
        <EmptyState title="Nothing waiting" body="No insurance cards are waiting for review." />
      ) : (
        <Box sx={{ display: 'grid', gap: 2 }}>
          {(items ?? []).map((p) => (
            <Paper key={p.id} sx={{ p: 2 }}>
              <InsuranceCard policy={p} showRunner onDecided={(m) => { setFlash(m); void load(); }} />
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
}
